import { Box } from "@mui/material";
import StatusSegment, { type SegmentFill } from "./StatusSegment";
import {
  AIRPELAGO_UPLOAD_STEP,
  LIDAR_UPLOAD_STATUSES,
  type LidarUploadStatusStep,
  STATUS_STEPS,
} from "./constants";
import { formatBytes } from "./formatBytes";
import { formatTimestamp } from "./formatTimestamp";
import type { ProjectLidarUploadStatus } from "./schemas";
import t from "./translations";

type Props = {
  projectStatus: ProjectLidarUploadStatus;
  reference: string;
};

function hasAirpelagoUpload(projectStatus: ProjectLidarUploadStatus): boolean {
  return projectStatus.airpelago_file_count > 0;
}

type StepCounts = { completed: number; total: number };

function stepCounts(
  projectStatus: ProjectLidarUploadStatus,
  step: LidarUploadStatusStep
): StepCounts | null {
  switch (step) {
    case "merged":
      return projectStatus.uploaded_file_source === "s3"
        ? null
        : {
            completed: projectStatus.merged_file_count,
            total: projectStatus.uploaded_file_count,
          };
    case "displayed":
      return {
        completed: projectStatus.displayed_scene_count,
        total: projectStatus.scene_count,
      };
    case "analysed":
      return {
        completed: projectStatus.analysed_scene_count,
        total: projectStatus.scene_count,
      };
    default:
      return null;
  }
}

const PROGRESS_LABELS: Partial<
  Record<LidarUploadStatusStep, (completed: number, total: number) => string>
> = {
  merged: t.MergedProgress,
  displayed: t.DisplayedProgress,
  analysed: t.AnalysedProgress,
};

function stepDetails(
  projectStatus: ProjectLidarUploadStatus,
  step: LidarUploadStatusStep
): { reachedAt: string | null; progress: string | null } {
  switch (step) {
    case "none":
      return hasAirpelagoUpload(projectStatus)
        ? {
            reachedAt: projectStatus.airpelago_uploaded_at,
            progress: t.AirpelagoBucketFiles(
              projectStatus.airpelago_file_count,
              formatBytes(projectStatus.airpelago_total_bytes)
            ),
          }
        : { reachedAt: null, progress: null };
    case "raw_uploaded":
      return {
        reachedAt: projectStatus.raw_uploaded_at,
        progress:
          projectStatus.raw_file_count > 0
            ? t.RawBucketFiles(
                projectStatus.raw_file_count,
                formatBytes(projectStatus.raw_total_bytes)
              )
            : t.UploadedProgress(projectStatus.uploaded_file_count),
      };
    default: {
      const counts = stepCounts(projectStatus, step);
      const progressLabel = PROGRESS_LABELS[step];
      return {
        reachedAt: projectStatus[`${step}_at`],
        progress:
          counts && progressLabel
            ? progressLabel(counts.completed, counts.total)
            : t.MergeNotTracked,
      };
    }
  }
}

function reachedLabel(reached: boolean, reachedAt: string | null): string {
  if (!reached) return t.NotReached;
  if (reachedAt === null) return t.NoTimeRecorded;
  return t.ReachedAt(formatTimestamp(reachedAt));
}

function partialFraction(counts: StepCounts | null): number | null {
  if (
    !counts ||
    counts.total === 0 ||
    counts.completed === 0 ||
    counts.completed >= counts.total
  )
    return null;
  return counts.completed / counts.total;
}

function segmentFill(
  step: LidarUploadStatusStep,
  stepIndex: number,
  currentIndex: number,
  airpelagoUpload: boolean
): SegmentFill {
  if (stepIndex === currentIndex) return "current";
  if (stepIndex > currentIndex) return "empty";
  return step === "none" && !airpelagoUpload ? "empty" : "completed";
}

export default function StatusBar({ projectStatus, reference }: Props) {
  const currentIndex = LIDAR_UPLOAD_STATUSES.indexOf(projectStatus.status);
  const airpelagoUpload = hasAirpelagoUpload(projectStatus);

  return (
    <Box
      data-testid={`lidar-upload-status.status-bar.${reference}`}
      data-status={projectStatus.status}
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(${LIDAR_UPLOAD_STATUSES.length}, 1fr)`,
        overflow: "hidden",
        borderRadius: "6px",
        bgcolor: "action.hover",
      }}
    >
      {LIDAR_UPLOAD_STATUSES.map((step, stepIndex) => {
        const stepStyle =
          step === "none" && airpelagoUpload
            ? AIRPELAGO_UPLOAD_STEP
            : STATUS_STEPS[step];
        const { reachedAt, progress } = stepDetails(projectStatus, step);
        const tooltipLines = [
          step === "none" && !airpelagoUpload
            ? null
            : reachedLabel(stepIndex <= currentIndex, reachedAt),
          progress,
        ].filter((line): line is string => line !== null);
        return (
          <StatusSegment
            key={step}
            step={step}
            label={stepStyle.label}
            color={stepStyle.color}
            textColor={stepStyle.textColor}
            fill={segmentFill(step, stepIndex, currentIndex, airpelagoUpload)}
            progressFraction={
              stepIndex > currentIndex
                ? partialFraction(stepCounts(projectStatus, step))
                : null
            }
            tooltipLines={tooltipLines}
          />
        );
      })}
    </Box>
  );
}
